import * as React from "react";
import {
    Box,
    Stack,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

const sampleRows = [
    {
        database: "e_coli",
        virulence_factor: "fimH",
        identity: 99.39,
        query_length: 489,
        template_length: 489,
        query_template: "489 / 489",
        protein_function: "Type 1 fimbriae",
        contig: "NODE_23_length_79987_cov_132.740621",
        position_in_contig: "40910..41398",
        accession: "NA",
        study_id: "20251112-1",
        sample_id: "YV40_scaffolds",
        section: "Virulence genes for Escherichia coli",
    },
    {
        database: "e_coli",
        virulence_factor: "fyuA",
        identity: 99.95,
        query_length: 2022,
        template_length: 2022,
        query_template: "2022 / 2022",
        protein_function: "Siderophore receptor",
        contig: "NODE_37_length_32514_cov_130.139316",
        position_in_contig: "27488..29509",
        accession: "CAY233333",
        study_id: "20251112-1",
        sample_id: "YV40_scaffolds",
        section: "Virulence genes for Escherichia coli",
    },
];

const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const mustQuote = /[",\n\r]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return mustQuote ? `"${escaped}"` : escaped;
};

const normalizeCell = (v) => {
    if (Array.isArray(v)) return v.join(", ");
    return v;
};

const objectToCSV = ({ columns = [], rows = [] }) => {
    const headers = columns.map((c) => escapeCSV(c.label ?? c.key ?? ""));
    const lines = [headers.join(",")];

    for (const r of rows) {
        const row = columns.map((c) => {
            const key = c.key;
            const value = key ? normalizeCell(r?.[key]) : "";
            return escapeCSV(value);
        });
        lines.push(row.join(","));
    }

    return lines.join("\r\n");
};

const rowsArrayToCSV = (rows = []) => {
    const keys = rows[0] ? Object.keys(rows[0]) : [];
    const headers = keys.map(escapeCSV);
    const lines = [headers.join(",")];

    for (const r of rows) {
        const line = keys.map((k) => escapeCSV(normalizeCell(r?.[k])));
        lines.push(line.join(","));
    }

    return lines.join("\r\n");
};

const safeFilename = (name) =>
    String(name || "virulence_factors")
        .trim()
        .replace(/[^\w\-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();

function ToolbarCSV({ data, rows }) {
    const csv = React.useMemo(() => {
        if (data?.columns && data?.rows) return objectToCSV(data);
        return rowsArrayToCSV(rows ?? []);
    }, [data, rows]);

    const filename = React.useMemo(() => {
        const base = safeFilename(data?.title) || "virulence_factors";
        return `${base}.csv`;
    }, [data?.title]);

    const copyCSV = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(csv);
                return;
            }
        } catch { }

        const ta = document.createElement("textarea");
        ta.value = csv;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    };

    const downloadCSV = () => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Tooltip title="Copiar CSV">
                <IconButton size="small" onClick={copyCSV}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
                <IconButton size="small" onClick={downloadCSV}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export default function VirulenceFactorTableMUI({ rows = sampleRows }) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ width: "100%" }}>
            <ToolbarCSV rows={rows} />

            <Paper
                elevation={2}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                }}
            >
                <TableContainer
                    sx={{
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                    }}
                >
                    <Table
                        stickyHeader
                        sx={{
                            minWidth: 1250,
                            tableLayout: "fixed",
                            "& th": {
                                bgcolor: "#000",
                                color: "#fff",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                            },
                            "& td, & th": {
                                borderColor: "divider",
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 140, minWidth: 140 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Database
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 170, minWidth: 170 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Virulence factor
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 110, minWidth: 110 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Identity
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 180, minWidth: 180 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Query/Template length
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 280, minWidth: 280 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Protein function
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 280, minWidth: 280 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Contig
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 180, minWidth: 180 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Position in contig
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((r, idx) => {
                                const key =
                                    r?.id ??
                                    r?.accession ??
                                    `${r?.sample_id ?? "sample"}-${r?.virulence_factor ?? "vf"}-${page * rowsPerPage + idx}`;

                                const queryTemplate =
                                    r?.query_template ??
                                    (r?.query_length != null && r?.template_length != null
                                        ? `${r.query_length} / ${r.template_length}`
                                        : "—");

                                return (
                                    <TableRow key={key}>
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight={700}>
                                                {r?.database ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight={700}>
                                                {r?.virulence_factor ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.identity ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {queryTemplate}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.protein_function ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.contig ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.position_in_contig ?? "—"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    sx={{ mt: "auto" }}
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );
}